import React, { useState, useEffect, useContext, createContext, useCallback } from 'react';
import { useFirebase } from '../Firebase';
import AuthUserContext from './context';

const AuthContext = createContext(null);

const getStoredAuthUser = () => {
  try {
    const stored = localStorage.getItem('authUser');
    return stored ? JSON.parse(stored) : null;
  } catch {
    localStorage.removeItem('authUser');
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const firebase = useFirebase();
  const [authUser, setAuthUser] = useState(getStoredAuthUser);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = firebase.onAuthUserListener(
      (user) => {
        localStorage.setItem('authUser', JSON.stringify(user));
        setAuthUser(user);
        setLoading(false);
      },
      () => {
        localStorage.removeItem('authUser');
        setAuthUser(null);
        setLoading(false);
      }
    );

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [firebase]);

  const refreshAuthUser = useCallback(async () => {
    const currentUser = firebase.auth.currentUser;
    if (!currentUser) {
      return null;
    }

    const profile = await firebase.fetchAuthUserProfile(currentUser);
    localStorage.setItem('authUser', JSON.stringify(profile));
    setAuthUser(profile);
    return profile;
  }, [firebase]);

  const value = { authUser, loading, refreshAuthUser };

  return (
    <AuthUserContext.Provider value={authUser}>
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    </AuthUserContext.Provider>
  );
};

export default AuthProvider;
